import type { PurchaseAttachment, PurchaseJobPlBucket, PurchasingPolicy } from '../types/purchasing';
import {
  crmUsersToResolverUsers,
  overlaySignedInApprover,
  resolveApprovalChain,
  type ApprovalResolverUser,
} from './purchasingApprovals';
import {
  OTHER_QUOTE_PAYMENT_METHODS,
  resolvePaymentMethodId,
  resolvePaymentMethodLabel,
} from './purchasingPolicy';
import { buildPurchaseCostObject } from './purchasingSpendClass';
import type { CRMUser } from '../types';

export { OTHER_QUOTE_PAYMENT_METHODS };

export type OtherQuotePayMethodId = 'company_card' | 'ach_office';

export function isOtherQuotePayMethodId(value: unknown): value is OtherQuotePayMethodId {
  return value === 'company_card' || value === 'ach_office';
}

export type SubmitOtherQuoteJobPurchaseInput = {
  requesterId: string;
  requesterName: string;
  users: ApprovalResolverUser[] | CRMUser[];
  policy: PurchasingPolicy;
  dealId: string;
  dealTitle?: string;
  timberProjectId?: string;
  projectManagerUserId?: string | null;
  vendorName: string;
  quoteLabel: string;
  amount: number;
  notes?: string;
  plBucket: PurchaseJobPlBucket;
  paymentMethodId: OtherQuotePayMethodId;
  rfqSubmissionId: string;
  rfqProjectId: string;
  quoteAttachment: PurchaseAttachment;
  /** Write the reverse link before self-issue so CF award ingest can skip. */
  afterCreate?: (purchaseRequisitionId: string) => Promise<void>;
};

export type SubmitOtherQuoteJobPurchaseResult = {
  purchaseRequisitionId: string;
  issued: boolean;
  paymentMethodId: OtherQuotePayMethodId;
  paymentMethod: string;
};

export function assertOtherQuoteJobPurchaseReady(
  input: Pick<
    SubmitOtherQuoteJobPurchaseInput,
    'requesterId' | 'users' | 'policy' | 'dealId' | 'amount' | 'paymentMethodId' | 'projectManagerUserId'
  >,
): void {
  if (!isOtherQuotePayMethodId(input.paymentMethodId)) {
    throw new Error('Choose Company Card or Bill Payment.');
  }
  if (!input.dealId.trim()) throw new Error('This quote must be tagged to a job.');
  const resolvedUsers =
    input.users.length && 'email' in input.users[0]
      ? crmUsersToResolverUsers(input.users as CRMUser[])
      : (input.users as ApprovalResolverUser[]);
  const requester = resolvedUsers.find(u => u.uid === input.requesterId);
  const users = requester ? overlaySignedInApprover(resolvedUsers, requester) : resolvedUsers;
  const resolved = resolveApprovalChain({
    requesterId: input.requesterId,
    amount: input.amount,
    costType: 'project',
    spendClass: 'job',
    projectManagerUserId: input.projectManagerUserId,
    users,
    policy: input.policy,
  });
  if (resolved.unresolved || !resolved.steps.length) {
    throw new Error(resolved.error || 'Could not resolve an approver for this quote.');
  }
}

export function buildOtherQuoteJobPurchaseDraft(input: SubmitOtherQuoteJobPurchaseInput) {
  const paymentMethod =
    OTHER_QUOTE_PAYMENT_METHODS.find(m => m.id === input.paymentMethodId)?.label
    || resolvePaymentMethodLabel(input.paymentMethodId);
  const costObject = buildPurchaseCostObject({
    spendClass: 'job',
    plBucket: input.plBucket,
    dealId: input.dealId,
    projectId: input.timberProjectId,
    jobLabel: input.dealTitle || 'Job',
  });
  const lineItems = [
    {
      id: 'line-1',
      description: input.quoteLabel,
      quantity: 1,
      unitPrice: input.amount,
      taxable: false,
    },
  ];
  const justification = input.notes?.trim() || `Other quote — ${input.quoteLabel}`;
  return { paymentMethod, costObject, lineItems, justification };
}

export function otherQuotePayMethodLabel(id: string | undefined): string {
  if (!id) return '';
  return resolvePaymentMethodLabel(id, OTHER_QUOTE_PAYMENT_METHODS) || id;
}

export function resolveOtherQuotePaymentMethodId(
  method: { id?: string; label?: string } | string | null | undefined,
): OtherQuotePayMethodId | undefined {
  const id = resolvePaymentMethodId(method, OTHER_QUOTE_PAYMENT_METHODS);
  return isOtherQuotePayMethodId(id) ? id : undefined;
}
